import RichText from '@/components/rich-text/RichText';
import { IQuestionListItem, ISubmitClozeAnswer, QuestionAnswerMode } from '@/types';
import React, { useMemo, useRef, useState } from 'react';
import Luckysheet from './luckysheet';
import QuestionUpload from '@/components/upload-img';
import DOMPurify from 'dompurify';
import { Input } from 'antd';
import { useRequest } from 'ahooks';
import { useAppStore } from '@/store';

interface IProps {
    onChangeValue?: (
        questionItem: IQuestionListItem,
        userAnswer: {
            userAnswer: string | number;
            userAnswerImagesList?: string[];
            excelAnswer?: string;
            userClozeAnswers?: ISubmitClozeAnswer[];
        },
        options?: { isFetch?: boolean }
    ) => void; // 选择题目
    questionItem: IQuestionListItem;
}

const ShortAnswer = (props: IProps) => {
    const { commonStore } = useAppStore();
    const { isOpenPersonalCorrect } = commonStore.common;
    const { questionItem } = props;
    const { questionId, userAnswer, excelAnswerContent, answerMode, aiCorrect } = questionItem;

    const excelJsonData = userAnswer?.excelAnswer ? userAnswer?.excelAnswer : excelAnswerContent;
    const luckysheetRef = useRef<any>();

    const isGwyCorrect = aiCorrect?.aiBotType === 2; // 是否开启 公务员批改
    const isCpaCorrect = aiCorrect?.cpaBotType === 3; // 是否开启 cpa批改
    const isOpenQuestionCorrect = !isOpenPersonalCorrect && (isCpaCorrect || isGwyCorrect);

    const [imageList, setImageList] = useState<string[]>(userAnswer?.userAnswerImagesList || []); // 图片
    const [excelAnswer, setExcelAnswer] = useState<string>(excelJsonData);
    const [inputValue, setInputValue] = useState<string | number>(userAnswer?.userAnswer || ''); // 填空

    const excelData = useMemo(() => {
        let data = null;
        try {
            data = JSON.parse(excelJsonData);
        } catch (error) {
            console.log('无表格数据');
        }
        return data;
    }, [excelAnswer]);

    const handleLuckysheetUpdated = () => {
        setExcelAnswer(JSON.stringify(luckysheetRef.current?.getAllSheets()));
        props.onChangeValue?.(questionItem, {
            userAnswer: inputValue,
            userAnswerImagesList: imageList,
            excelAnswer: JSON.stringify(luckysheetRef.current?.getAllSheets()),
        });
    };

    const handleInputChange = (value: string) => {
        // xss 过滤
        const content = DOMPurify.sanitize(value);
        setInputValue(content);
        props.onChangeValue?.(
            questionItem,
            {
                userAnswer: content,
                excelAnswer: excelAnswer,
                userAnswerImagesList: imageList,
            },
            { isFetch: false }
        );
        dealyRunChange(content);
    };

    const { run: dealyRunChange } = useRequest(
        (content: string) => {
            return new Promise(resolve => {
                resolve(
                    props.onChangeValue?.(questionItem, {
                        userAnswer: content,
                        excelAnswer: excelAnswer,
                        userAnswerImagesList: imageList,
                    })
                );
            });
        },
        {
            debounceWait: 3000,
            manual: true,
        }
    );

    const onChangeImage = (uploadImgs: string[]) => {
        setImageList(uploadImgs);
        props.onChangeValue?.(questionItem, {
            userAnswer: inputValue,
            userAnswerImagesList: uploadImgs,
            excelAnswer: excelAnswer, // answerMode === 3 || 4
        });
    };

    return (
        <>
            {[QuestionAnswerMode.text].includes(answerMode) && (
                <Input.TextArea
                    autoSize={{ minRows: 4 }}
                    maxLength={5000}
                    onChange={e => handleInputChange(e.target.value)}
                    placeholder="请回答"
                    showCount
                    value={inputValue}
                />
            )}

            {[QuestionAnswerMode.word].includes(answerMode) && (
                <RichText
                    key={`sub-tiku__question-rich-content-${questionId}`}
                    onchange={value => {
                        handleInputChange(value);
                    }}
                    rowKey="sub-tiku__question-rich-content"
                    value={inputValue}
                />
            )}

            {[QuestionAnswerMode.excel, QuestionAnswerMode.excelLucky].includes(answerMode) && (
                <Luckysheet cellUpdated={() => handleLuckysheetUpdated()} excelData={excelData} key={questionId} sheetRef={luckysheetRef} />
            )}

            {[QuestionAnswerMode.excelAndText].includes(answerMode) && (
                <>
                    <Luckysheet
                        cellUpdated={() => handleLuckysheetUpdated()}
                        excelData={excelData}
                        key={questionId}
                        sheetRef={luckysheetRef}
                        style={{ marginBottom: 20 }}
                    />

                    <Input.TextArea
                        autoSize={{ minRows: 4 }}
                        maxLength={5000}
                        onChange={e => handleInputChange(e.target.value)}
                        placeholder="请回答"
                        showCount
                        value={inputValue}
                    />
                </>
            )}

            {[QuestionAnswerMode.textAndExcel].includes(answerMode) && (
                <>
                    <Input.TextArea
                        autoSize={{ minRows: 4 }}
                        maxLength={5000}
                        onChange={e => handleInputChange(e.target.value)}
                        placeholder="请回答"
                        showCount
                        value={inputValue}
                        style={{ marginBottom: 20 }}
                    />
                    <Luckysheet
                        cellUpdated={() => handleLuckysheetUpdated()}
                        excelData={excelData}
                        key={questionId}
                        sheetRef={luckysheetRef}
                    />
                </>
            )}

            {isOpenQuestionCorrect && isGwyCorrect && (
                <div className="sub-tiku_question-short-answer-tip">提示:要使用智能批改功能，您需要输入至少30个字的文字版答案</div>
            )}
            {isOpenQuestionCorrect && isCpaCorrect && (
                <div className="sub-tiku_question-short-answer-tip">
                    *这是一个智能批改题，如果需要批改，请在文本框或表格内键入回答。图片不能作为批改内容。
                </div>
            )}

            <QuestionUpload imageList={imageList} onChangeImage={onChangeImage} />
        </>
    );
};

export default ShortAnswer;
